// Vendor register reads on the shared apiClient (base URL, auth, error semantics).
//
// `/vendors` is gated on the VENDORS screen — the same screen that gates the
// vendor-invoice reconcile action, so anyone who can confirm a supplier on an
// invoice can also search for one. Supplier PICKERS read `/vendors/options`
// instead (see `fetchVendorOptions`).
import { get, patch, postJson } from './apiClient';

/** What a supplier search result has to show to be picked confidently. */
export interface VendorSearchResult {
  _id: string;
  name: string;
  code?: string;
  email?: string;
  phone?: string;
  address?: string;
  creditDays?: number;
  isActive?: boolean;
}

/**
 * Search the vendor register by name, email, phone, address or alias.
 *
 * The backend does the matching (`vendor.controller.listVendors`, one
 * case-insensitive regex over those five fields) — deliberately NOT the
 * reconciliation scorer. This is a human looking for a company they know
 * exists; the 0.9 auto-match bar and the 0.55 candidate floor are there to stop
 * the MACHINE choosing, and applying them to a typed search would hide exactly
 * the supplier the reviewer is trying to find.
 *
 * An empty term returns the whole register, which is what the caller wants for
 * a first render.
 */
export async function searchVendors(term: string): Promise<VendorSearchResult[]> {
  const q = term.trim();
  const query = q ? `?search=${encodeURIComponent(q)}` : '';
  const data = await get<any>(`/vendors${query}`);
  const list = data?.data;
  if (!Array.isArray(list)) {
    // Same contract-break rule as the other API modules: say so once, loudly,
    // rather than rendering "there are no suppliers".
    // eslint-disable-next-line no-console
    console.warn('[vendorsApi] unexpected /vendors response shape', data);
    return [];
  }
  return list;
}

/**
 * One supplier from the register, or null when it is gone (deleted, or never
 * existed). USC-BUG-0031: the item memory panel reads the Payment Terms on file
 * from here; they live once, on the vendor.
 */
export async function getVendor(vendorId: string): Promise<VendorSearchResult | null> {
  const data = await get<any>(`/vendors?id=${encodeURIComponent(vendorId)}`);
  const list = data?.data;
  if (!Array.isArray(list)) {
    // eslint-disable-next-line no-console
    console.warn('[vendorsApi] unexpected /vendors response shape', data);
    return null;
  }
  return list[0] ?? null;
}

/**
 * Set a supplier's Payment Terms (credit days; 0 = not on file) through the one
 * vendor update route, which also re-derives the payment-terms check on every
 * invoice from that supplier (`refreshPaymentTermsForVendor`). Sends ONLY the
 * terms, so it is not the D35 review of an auto-created supplier.
 */
export async function setVendorCreditDays(vendorId: string, creditDays: number): Promise<VendorSearchResult> {
  const res = await patch<any>(`/vendors/${encodeURIComponent(vendorId)}`, { creditDays });
  return res?.data;
}

/** One entry of the strict supplier list (D86): the only names a picker may emit. */
export interface VendorOption {
  _id: string;
  name: string;
  code?: string;
}

// D86: one request shared by every picker on the screen; a settled list is reused for 30 s so a vendor
// just added on the Vendors page shows up on the next screen.
const OPTIONS_TTL_MS = 30000;
let optionsLoad: Promise<VendorOption[]> | null = null;
let optionsAt = 0;

/**
 * The vendor list every supplier PICKER reads (GET /vendors/options, D86): active,
 * non-deleted, placeholder names removed, sorted by name. Open to anyone who may view a
 * screen that hosts a picker, unlike /vendors (vendors:view). Throws on failure - a failed load must not read as "no vendors".
 * Shared: see above. A failed load is dropped, so the next mount retries.
 */
export function fetchVendorOptions(): Promise<VendorOption[]> {
  if (!optionsLoad || Date.now() - optionsAt > OPTIONS_TTL_MS) {
    const load = loadVendorOptions();
    optionsLoad = load;
    optionsAt = Date.now();
    load.catch(() => {
      if (optionsLoad === load) optionsLoad = null;
    });
  }
  return optionsLoad;
}

/** Test hook: forget the shared list. */
export function resetVendorOptionsCache(): void {
  optionsLoad = null;
}

/** What POST /vendors takes (the Vendors-page form's payload); only the name is required (D86). */
export interface NewVendorPayload {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  creditDays?: number;
  code?: string;
  isActive?: boolean;
  priceListUrl?: string;
  hideVesselOnPo?: boolean;
}

/**
 * Create a supplier (D107): the ONE client call behind the Vendors page and every "+ Create new
 * vendor" popup. The server refuses a name that looks like a vendor already on file
 * (409 VENDOR_LOOKALIKE); the caller asks the person and resends with `confirmDistinct`.
 * Drops the shared options list on success, so a picker that mounts next shows the new vendor.
 * Needs vendors:create. Throws on any failure (the apiClient error, with its `code`).
 */
export async function createVendor(
  payload: NewVendorPayload,
  opts: { confirmDistinct?: boolean } = {},
): Promise<{ _id: string; name: string; code?: string }> {
  const res = await postJson<any>('/vendors', opts.confirmDistinct ? { ...payload, confirmDistinct: true } : payload);
  const created = res?.data;
  if (!created?._id) throw new Error('unexpected POST /vendors response shape');
  resetVendorOptionsCache();
  return { _id: created._id, name: created.name ?? payload.name, code: created.code };
}

async function loadVendorOptions(): Promise<VendorOption[]> {
  const data = await get<any>('/vendors/options');
  const list = data?.data?.vendors;
  if (!Array.isArray(list)) {
    throw new Error('unexpected /vendors/options response shape');
  }
  return list;
}
